import {
  entityPaths,
  entityTextSpec,
  rad2deg,
  type Entity,
  type Layer,
} from '@lakaran/geometry';
import type { CadDocument } from './document';

/** AutoCAD Color Index approximation: light geometry on dark backgrounds. */
const DEFAULT_COLOR_INDEX = 7;

function pair(code: number, value: string | number): string {
  return `${code}\n${value}\n`;
}

function layerTable(doc: CadDocument): string {
  const layers = [...doc.layers.values()];
  let out = pair(0, 'SECTION') + pair(2, 'TABLES');
  out += pair(0, 'TABLE') + pair(2, 'LAYER') + pair(70, layers.length);
  for (const layer of layers) {
    out += pair(0, 'LAYER');
    out += pair(2, layer.name);
    out += pair(70, 0);
    out += pair(62, DEFAULT_COLOR_INDEX);
    out += pair(6, 'CONTINUOUS');
  }
  out += pair(0, 'ENDTAB');
  out += pair(0, 'ENDSEC');
  return out;
}

function polyline(layer: string, points: { x: number; y: number }[], closed: boolean): string {
  let out = pair(0, 'POLYLINE');
  out += pair(8, layer);
  out += pair(66, 1);
  out += pair(70, closed ? 1 : 0);
  for (const point of points) {
    out += pair(0, 'VERTEX');
    out += pair(8, layer);
    out += pair(10, point.x);
    out += pair(20, point.y);
  }
  out += pair(0, 'SEQEND');
  out += pair(8, layer);
  return out;
}

function entityToDxf(entity: Entity, layerName: string): string {
  const layer = layerName;
  switch (entity.type) {
    case 'line':
      return (
        pair(0, 'LINE') +
        pair(8, layer) +
        pair(10, entity.a.x) +
        pair(20, entity.a.y) +
        pair(11, entity.b.x) +
        pair(21, entity.b.y)
      );
    case 'polyline':
      return polyline(layer, entity.vertices, entity.closed);
    case 'circle':
      return (
        pair(0, 'CIRCLE') +
        pair(8, layer) +
        pair(10, entity.center.x) +
        pair(20, entity.center.y) +
        pair(40, entity.radius)
      );
    case 'arc':
      return (
        pair(0, 'ARC') +
        pair(8, layer) +
        pair(10, entity.center.x) +
        pair(20, entity.center.y) +
        pair(40, entity.radius) +
        pair(50, rad2deg(entity.startAngle)) +
        pair(51, rad2deg(entity.endAngle))
      );
    case 'text': {
      const spec = entityTextSpec(entity);
      if (!spec) return '';
      return (
        pair(0, 'TEXT') +
        pair(8, layer) +
        pair(10, spec.position.x) +
        pair(20, spec.position.y) +
        pair(40, spec.height) +
        pair(1, spec.text) +
        pair(50, rad2deg(spec.rotation))
      );
    }
    default:
      // Ellipses, hatches and dimensions are flattened to polylines.
      return entityPaths(entity, { chordTolerance: 0.05 })
        .map((path) => polyline(layer, path.points, path.closed))
        .join('');
  }
}

/** Minimal DXF (AC1009 / R12) export: layers, lines, polylines, circles, arcs, text. */
export function documentToDxf(doc: CadDocument): string {
  let out = '';
  out += pair(0, 'SECTION') + pair(2, 'HEADER');
  out += pair(9, '$ACADVER') + pair(1, 'AC1009');
  out += pair(9, '$INSUNITS') + pair(70, 4); // millimetres
  out += pair(0, 'ENDSEC');
  out += layerTable(doc);

  out += pair(0, 'SECTION') + pair(2, 'ENTITIES');
  for (const entity of doc.all()) {
    const layer: Layer | undefined = doc.getLayer(entity.layerId);
    if (!layer || !layer.visible) continue;
    out += entityToDxf(entity, layer.name);
  }
  out += pair(0, 'ENDSEC');
  out += pair(0, 'EOF');
  return out;
}
